import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isNil } from 'lodash-es';
import { useObserver } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { apiClient, getEndpoints } from '../../api/common';
import {
	NOTIFICATION_NAMESPACE,
	notificationUnreadCountQueryKeyFactory,
} from '../../api/hooks/notification/constants';
import { invalidateNotificationListQuery } from '../../api/hooks/notification/utils';
import { useFeatureFlags } from '../../utils/featureFlags';
import { checkFeatureFlag } from '../../utils/featureFlags/featureFlagUtils';
import { NOTIFICATION_EVENT_TYPES } from './constants';
import { inboxFeatureStore } from './store';
import { getFilters, getInboxFiltersFromUrl } from './utils/filters';

export const useMarkAsRead = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			id,
			all = false,
			events = '',
			status = true,
		}: {
			id?: string;
			all?: boolean;
			events?: string;
			status?: boolean;
		}) => {
			if (!all && isNil(id)) {
				throw new Error('Notification ID is required');
			}

			const path = all ? 'read_all' : 'read';
			const url = getEndpoints(NOTIFICATION_NAMESPACE).byPath([path]);
			const params = all ? { events } : { id, status };

			const { data } = await apiClient.post(url, {}, { params });
			return data;
		},
		onSuccess: (data, variables) => {
			if (variables.all === true) {
				invalidateNotificationListQuery();
				queryClient.invalidateQueries({
					queryKey: notificationUnreadCountQueryKeyFactory.all(),
				});
			} else if (!isNil(variables.id)) {
				// Only invalidate if we're showing read notifications OR if this notification isn't recently read
				const shouldInvalidate =
					inboxFeatureStore.showRead ||
					!inboxFeatureStore.isRecentlyRead(variables.id);

				if (shouldInvalidate) {
					invalidateNotificationListQuery();
				}

				// Update unread count
				const notificationCountQueryData: Record<'count', number> | undefined =
					queryClient.getQueryData(
						notificationUnreadCountQueryKeyFactory.all()
					);

				if (!isNil(notificationCountQueryData)) {
					queryClient.setQueryData(
						notificationUnreadCountQueryKeyFactory.all(),
						{
							...notificationCountQueryData,
							count: variables.status
								? notificationCountQueryData.count - 1
								: notificationCountQueryData.count + 1,
						}
					);
				}
			}
		},
	});
};

export function useInboxEventTypes() {
	const featureFlags = useFeatureFlags();

	return NOTIFICATION_EVENT_TYPES.filter(
		(n) => !n.featureFlag || checkFeatureFlag(n.featureFlag, featureFlags)
	);
}

export const useInboxFeature = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const inboxEventTypes = useInboxEventTypes();

	const { searchTerm, page, events, isInclusive, selectedId } =
		getInboxFiltersFromUrl(searchParams);

	const setSearchTerm = useCallback(
		(term: string) => {
			setSearchParams((prev) => {
				const newParams = new URLSearchParams(prev);
				if (term) {
					newParams.set('searchTerm', term);
				} else {
					newParams.delete('searchTerm');
				}
				return newParams;
			});
		},
		[setSearchParams]
	);

	const setEvents = useCallback(
		(eventValue: string) => {
			setSearchParams((prev) => {
				const newParams = new URLSearchParams(prev);
				if (eventValue) {
					newParams.set('events', eventValue);
				} else {
					newParams.delete('events');
				}
				return newParams;
			});
		},
		[setSearchParams]
	);

	const setIsInclusive = useCallback(
		(inclusive: boolean) => {
			setSearchParams((prev) => {
				const newParams = new URLSearchParams(prev);
				newParams.set('inclusive', inclusive.toString());
				return newParams;
			});
		},
		[setSearchParams]
	);

	const setSelectedId = useCallback(
		(id: string) => {
			setSearchParams((prev) => {
				const newParams = new URLSearchParams(prev);
				if (id) {
					newParams.set('id', id);
				} else {
					newParams.delete('id');
				}
				return newParams;
			});
		},
		[setSearchParams]
	);

	return useObserver(() => {
		const filters = getFilters(
			inboxEventTypes,
			events,
			searchTerm,
			isInclusive,
			inboxFeatureStore.showRead
		);

		return {
			searchTerm,
			setSearchTerm,
			page,
			events,
			setEvents,
			isInclusive,
			setIsInclusive,
			selectedId,
			setSelectedId,
			filters,
		};
	});
};
